import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';

import NameWithCode from '@features/campaigns/shared/components/NameWithCode/NameWithCode';
import { SectionWrapper } from '@features/campaigns/shared/components/Review/SectionWrapper/SectionWrapper';
import { CampaignDetailsData } from '@features/campaigns/shared/containers/Review/Review.dto';
import { ReviewSectionProps } from '@features/campaigns/shared/containers/Review/types';
import { formatCampaignCountryName } from '@features/campaigns/shared/utils/formatCampaignCountryName.ts';
import { AccessRights } from '@shared/api/commonApiInterfaces.ts';
import formFieldsStyles from '@shared/components/FormFields/FormFields.module.scss';
import { Item } from '@shared/components/Review/Item/Item';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';
import { formatNumberAsMoney } from '@shared/utils/moneyFormat.ts';

export function CampaignDetailsExternal({
  headerText,
  ctaButtonCallback,
  data,
  accessRights,
}: ReviewSectionProps<CampaignDetailsData>) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const { isSuperAdminUser } = useUserDetails();
  const formattedBudget = formatNumberAsMoney(data.budget);
  const formattedStartDate = format(new Date(data.startDate), 'dd/MM/yyyy');
  const formattedEndDate = format(new Date(data.endDate), 'dd/MM/yyyy');
  const formattedDateRange = `${formattedStartDate} - ${formattedEndDate}`;
  const formattedCountryName = t(formatCampaignCountryName(data.country), { ns: i18nNameSpace.COMMON });

  const canReadAdvertiserAndBrandCode = Boolean(accessRights?.includes(AccessRights.READ_ADVERTISER_BRAND_CODE));

  return (
    <SectionWrapper headerText={headerText} ctaButtonCallback={ctaButtonCallback}>
      <div className='grid' data-testid='campaign-details-external-section'>
        <div className='row'>
          <div className={isSuperAdminUser ? 'col-6' : 'col-12'}>
            <Item label={t('builder.formFields.campaignName.label')} value={data.campaignName} />
          </div>
          {isSuperAdminUser && (
            <div className='col-6'>
              <Item label={t('builder.formFields.country.label')} value={formattedCountryName} />
            </div>
          )}
        </div>
        <div className='row'>
          <div className='col-6'>
            <Item label={t('builder.formFields.budget.label')} value={formattedBudget} />
          </div>
          <div className='col-6'>
            <Item label={t('builder.formFields.daterange.submitSectionLabel')} value={formattedDateRange} />
          </div>
        </div>

        <div className='row'>
          <div className='col-12'>
            <hr className={formFieldsStyles.divider} />
          </div>
        </div>

        <div className='row'>
          <div className={'col-6'}>
            <Item
              label={t('builder.formFields.advertiser.label')}
              value={
                <NameWithCode
                  name={data.advertiserName}
                  code={data.advertiserCode}
                  showCode={canReadAdvertiserAndBrandCode}
                />
              }
            />
          </div>
          <div className={'col-6'}>
            <Item
              label={t('builder.formFields.billingContact.label')}
              value={
                <NameWithCode
                  name={data.billingContactName}
                  code={data.billingContactCode}
                  showCode={canReadAdvertiserAndBrandCode}
                />
              }
            />
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}
